/**
 * AI cost accounting and usage-rollup shapes, shared by the Workers (which
 * compute and enforce) and the web apps (which render).
 *
 * @shared-compat: one cost table must serve every AI-spending Worker and every
 * surface that reports spend. If pricing or the micro-USD conversion were
 * duplicated per app, a budget enforced in one Worker and a total rendered in
 * another would disagree — and the disagreement would be silent.
 *
 * ## Why micro-USD
 *
 * `ai_logs.cost_cents` is an `integer` and the original cost helper rounded to
 * whole cents. A short chat turn costs a fraction of a cent, so it rounded to
 * **zero** — a daily budget summed from that column would never trip no matter
 * how many turns were served.
 *
 * The authoritative unit is therefore micro-USD (millionths of a dollar), which
 * keeps four more decimal places than cents and stays an exact integer. Rounded
 * cents are still written to `cost_cents` so existing readers keep working.
 */

/** Micro-USD in one cent. Used to read historical `cost_cents`-only rows. */
export const MICRO_USD_PER_CENT = 10_000;

/** Micro-USD in one dollar. */
export const MICRO_USD_PER_USD = 1_000_000;

/**
 * Pricing itself lives in `@command-center/logging-sdk/pricing`, beside the
 * `ai_logs` shapes it exists to populate.
 *
 * It moved there because the MPF Worker writes `ai_logs` and does NOT depend on
 * this package — leaving the table here meant either a second copy of the rates
 * or dragging the whole One Up domain bundle into a Worker on a subrequest
 * budget. Two copies of a price table is how a budget enforced in one Worker
 * and a total rendered in another disagree, silently.
 *
 * Re-exported here so the many existing `@oneup/shared` importers keep working.
 */
export {
  AI_MODEL_PRICING,
  AI_PRICING_VERSION,
  aiModelPrice,
  canonicalAiModel,
  computeAiCostMicroUsd,
  isPricedAiModel,
  priceAiCall,
  pricedAiModels,
  type AiModelPrice,
  type AiPriceConfidence,
  type AiPricedCall,
} from '@command-center/logging-sdk/pricing';

/** Rounded cents, for the legacy `ai_logs.cost_cents` column. */
export function microUsdToCents(microUsd: number | null | undefined): number | null {
  if (typeof microUsd !== 'number' || !Number.isFinite(microUsd)) return null;
  return Math.max(0, Math.round(microUsd / MICRO_USD_PER_CENT));
}

/**
 * Best available precise cost for a logged row. Prefers `cost_micro_usd`;
 * falls back to widening `cost_cents` for rows written before the precise
 * column existed.
 */
export function rowCostMicroUsd(row: {
  cost_micro_usd?: number | null;
  cost_cents?: number | null;
}): number {
  if (typeof row.cost_micro_usd === 'number' && Number.isFinite(row.cost_micro_usd)) {
    return row.cost_micro_usd;
  }
  if (typeof row.cost_cents === 'number' && Number.isFinite(row.cost_cents)) {
    return row.cost_cents * MICRO_USD_PER_CENT;
  }
  return 0;
}

export function microUsdToUsd(microUsd: number): number {
  return microUsd / MICRO_USD_PER_USD;
}

export function usdToMicroUsd(usd: number): number {
  return Math.round(usd * MICRO_USD_PER_USD);
}

/**
 * Render micro-USD for people. Sub-cent amounts keep enough precision to be
 * meaningful ($0.0075) instead of collapsing to $0.00, which is exactly the
 * failure the precise column exists to avoid.
 */
export function formatMicroUsd(microUsd: number | null | undefined): string {
  if (typeof microUsd !== 'number' || !Number.isFinite(microUsd)) return '—';
  const usd = microUsdToUsd(microUsd);
  if (usd === 0) return '$0.00';
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  if (usd < 1) return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(2)}`;
}

// ---------- rollup shapes ----------

export interface AiUsageModelRollup {
  /** OpenAI model id, or 'unknown' for rows logged without one. */
  model: string;
  /** Human label from the shared allowlist when known, else the raw id. */
  label: string;
  turns: number;
  tokensInput: number;
  tokensOutput: number;
  costMicroUsd: number;
  /** True when at least one row used a model we cannot price. */
  hasUnpricedUsage: boolean;
}

export interface AiUsageWindow {
  turns: number;
  tokensInput: number;
  tokensOutput: number;
  costMicroUsd: number;
}

export interface AiUsageBudget {
  /** Daily ceiling in micro-USD, or null when uncapped. */
  limitMicroUsd: number | null;
  spentMicroUsd: number;
  /** Remaining allowance, floored at 0. Null when uncapped. */
  remainingMicroUsd: number | null;
  /** 0–1, or null when uncapped. */
  usedFraction: number | null;
  /** ISO timestamp the daily window resets (next UTC midnight). */
  resetsAt: string;
}

export interface AiUsageSummary {
  today: AiUsageWindow;
  last7Days: AiUsageWindow;
  last30Days: AiUsageWindow;
  byModel: AiUsageModelRollup[];
  budget: AiUsageBudget;
}

export interface AiActivityEntry {
  id: string;
  createdAt: string;
  status: string;
  feature: string | null;
  model: string | null;
  modelLabel: string | null;
  tokensInput: number | null;
  tokensOutput: number | null;
  costMicroUsd: number;
  /** Short, already-truncated prompt preview. Never the raw prompt. */
  promptPreview: string | null;
  /** Short, already-truncated response preview. Never the raw output. */
  responsePreview: string | null;
  errorMessage: string | null;
}

/** Next UTC midnight, when a daily budget window rolls over. */
export function nextDailyBudgetReset(now: Date = new Date()): string {
  const reset = new Date(now);
  reset.setUTCHours(24, 0, 0, 0);
  return reset.toISOString();
}

/** Start of the current UTC day, the lower bound of a daily budget window. */
export function startOfUtcDay(now: Date = new Date()): string {
  const start = new Date(now);
  start.setUTCHours(0, 0, 0, 0);
  return start.toISOString();
}

export function buildAiUsageBudget(
  spentMicroUsd: number,
  limitMicroUsd: number | null,
  now: Date = new Date(),
): AiUsageBudget {
  return {
    limitMicroUsd,
    spentMicroUsd,
    remainingMicroUsd: limitMicroUsd === null ? null : Math.max(0, limitMicroUsd - spentMicroUsd),
    usedFraction:
      limitMicroUsd === null || limitMicroUsd <= 0
        ? null
        : Math.min(1, spentMicroUsd / limitMicroUsd),
    resetsAt: nextDailyBudgetReset(now),
  };
}
